import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { Table } from "../../../table/Table";
import { RT8Pagination } from "../../../table/Pagination";
import { SearchInput } from "../../../fields/base/TextField";
import { ProductCell } from "../../../../modules/merchandising/components/cells/ProductCell";
import { type GVProduct } from "../../../../zodios/common/gvProductSchema";
import { Flex, VStack } from "../../../../../styled-system/jsx";
import { useClientTable } from "../../../table/tableFeatures";
import { selectedIdsToSelectionState } from "../../../table/utils";

type StorePageProductSelectorProps = {
  products: GVProduct[];
  selectedProductIds?: Array<number | string> | null;
  multi?: boolean;
  onSelectionChange: (selectedIds: string[]) => void;
  isDisabled?: boolean;
  pageSize?: number;
  isLoading?: boolean;
  isReview?: boolean;
};

const StorePageProductSelector = ({
  products,
  selectedProductIds,
  multi = false,
  onSelectionChange,
  isDisabled = false,
  pageSize = 5,
  isLoading = false,
  isReview = false,
}: StorePageProductSelectorProps) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [globalFilter, setGlobalFilter] = useState("");
  const rowSelection = useMemo(() => selectedIdsToSelectionState(selectedProductIds), [selectedProductIds]);

  const filteredProducts = useMemo(() => {
    if (isReview) {
      return products.filter((product) => selectedProductIds?.includes(product.id));
    }
    return products;
  }, [products, isReview, selectedProductIds]);

  const columns = useMemo(
    () => [
      {
        id: "product_title",
        accessorKey: "product_title",
        header: () => <span>{t(($) => $.advertising["Product"])}</span>,
        enableHiding: false,
        minSize: 250,
        size: 670,
        cell: ({ row, getValue }: { row: { original: GVProduct }; getValue: () => string }) => {
          const product = row.original;
          return (
            <ProductCell
              original={{
                product_id: product.id.toString(),
                sales_state: product.sales_state,
                stock_status: product.stock_status ?? "IN_STOCK",
                inventory_state: product.inventory_state,
                pricing_plan__status: null,
                asin: product.asin,
                sku: product.sku,
                tags: product.tags ?? [],
                primary_variant: null,
                cellContext: "merchandising",
              }}
              value={getValue()}
              showInventoryBadge={false}
              className={css({ marginRight: "auto", overflowX: "hidden" })}
              linkToNewTab={true}
            />
          );
        },
      },
    ],
    [t],
  );

  const table = useClientTable({
    data: filteredProducts,
    columns,
    getRowId: (row) => row.id.toString(),
    enableGlobalFilter: true,
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue) {
        return true;
      }
      const searchable = filterValue.toLowerCase();
      return `${row.original.id}${row.original.asin}${row.original.sku}${row.original.product_title}`
        .toLowerCase()
        .includes(searchable);
    },
    enableRowSelection: () => !isDisabled,
    enableMultiRowSelection: multi,
    state: {
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize },
      rowSelection: selectedIdsToSelectionState(selectedProductIds),
    },
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      const normalizedIds =
        multi ? updatedIds
        : updatedIds.length > 1 ? [updatedIds[updatedIds.length - 1]]
        : updatedIds;
      onSelectionChange(normalizedIds.map((id) => String(id)).filter(Boolean));
    },
    onGlobalFilterChange: setGlobalFilter,
    meta: {
      showSkeleton: isLoading,
    },
  });

  return (
    <VStack css={{ alignItems: "flex-start", width: "90%" }}>
      <Flex className={css({ gap: "large", justifyContent: "space-between", alignItems: "center", width: "100%" })}>
        <Flex className={css({ gap: "large", alignItems: "center", minWidth: 0 })}>
          <span className={css({ fontSize: "1.2rem", fontWeight: 600 })}>
            {t(($) => $.advertising["Products Selected"], { count: Object.keys(rowSelection).length })}
          </span>
          <SearchInput
            aria-label={t(($) => $.common.actions.Search)}
            isExpandable
            css={{ flex: "0 1 350px" }}
            value={table.state.globalFilter}
            onChange={table.setGlobalFilter}
          />
        </Flex>
        <div className={css({ flexShrink: 0 })}>
          <RT8Pagination table={table} />
        </div>
      </Flex>
      <span className={css({ marginRight: "auto", padding: "small", fontSize: "small" })}>
        {t(($) => $.advertising["* Note that performance stats are not shown for store landing page products"])}
      </span>
      <Table table={table} enableSelectAll={false} />
    </VStack>
  );
};

export { StorePageProductSelector };
